import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { GraduationCap, Loader2, BookOpen, MessageCircleQuestion, BarChart3, LogOut, Sparkles, Flame, FileText, Target, X, ClipboardCheck, Lock, Crown } from "lucide-react";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";
import { subjectLabel, BOARD_LABELS, JEE_SUBJECTS, NEET_SUBJECTS } from "../components/tutor/subjectLabels";
import { TutorOnboarding } from "../components/tutor/TutorOnboarding";
import { MockTest } from "../components/tutor/MockTest";
import { CompetitiveQuiz } from "../components/tutor/CompetitiveQuiz";
import { TutorChat } from "../components/tutor/TutorChat";
import { TutorProgress } from "../components/tutor/TutorProgress";
import { BadgeCelebration } from "../components/tutor/BadgeCelebration";
import { TutorPlans } from "../components/tutor/TutorPlans";
import { PAYWALL_EVENT, installPaywallInterceptor, errMsg } from "../components/tutor/paywall";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TutorPage() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [profile, setProfile] = useState(null);
  const [syllabus, setSyllabus] = useState(null);
  const [tab, setTab] = useState("learn");
  const [subject, setSubject] = useState("maths");
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [streakInfo, setStreakInfo] = useState(null);
  const [newBadges, setNewBadges] = useState(null);
  const [starting, setStarting] = useState("");
  const [compSetup, setCompSetup] = useState(false);
  const [compExam, setCompExam] = useState("jee");
  const [compChapter, setCompChapter] = useState("");
  const [compQuiz, setCompQuiz] = useState(null);
  const [mockSetup, setMockSetup] = useState(false);
  const [mockChapter, setMockChapter] = useState("");
  const [mockTest, setMockTest] = useState(null);
  const [signedOut, setSignedOut] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [entData, setEntData] = useState(null);
  const [plansOpen, setPlansOpen] = useState(null);
  const [paidBanner, setPaidBanner] = useState("");

  const getAuthConfig = useCallback(() => {
    const stored = sessionStorage.getItem("user_data");
    if (!stored) return {};
    try {
      const data = JSON.parse(stored);
      if (data.session_token) return { headers: { Authorization: `Bearer ${data.session_token}` } };
    } catch {}
    return { withCredentials: true };
  }, []);

  useEffect(() => {
    const stateUser = location.state?.user;
    const stored = sessionStorage.getItem("user_data");
    if (stateUser) setUser(stateUser);
    else if (stored) { try { setUser(JSON.parse(stored)); } catch {} }
    const cfg = (() => {
      try {
        const d = JSON.parse(stored || "{}");
        return d.session_token ? { headers: { Authorization: `Bearer ${d.session_token}` } } : { withCredentials: true };
      } catch { return {}; }
    })();
    axios.get(`${API}/user-auth/me`, cfg)
      .then((r) => setUser((u) => ({ ...(u || {}), ...r.data })))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
    axios.get(`${API}/tutor/syllabus`).then((r) => setSyllabus(r.data)).catch(() => {});
  }, [location.state, getAuthConfig]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/tutor/profile`, getAuthConfig())
      .then((r) => setProfile(r.data?.profile || false))
      .catch(() => setProfile(false));
    loadSessions();
    loadEntitlement();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const uninstall = installPaywallInterceptor();
    const onPaywall = (e) => setPlansOpen({ reason: e.detail?.message || "Upgrade to unlock this feature." });
    window.addEventListener(PAYWALL_EVENT, onPaywall);
    return () => { uninstall(); window.removeEventListener(PAYWALL_EVENT, onPaywall); };
  }, []);

  useEffect(() => {
    const orderId = new URLSearchParams(location.search).get("sub_order");
    if (!orderId || !user) return;
    axios.post(`${API}/subscriptions/verify-payment`, { order_id: orderId }, getAuthConfig())
      .then((r) => {
        if (r.data?.status === "paid") {
          setPaidBanner(`${r.data.subscription?.plan_name || "Your plan"} is active — happy learning!`);
          loadEntitlement();
        }
      })
      .catch(() => {})
      .finally(() => window.history.replaceState({}, "", "/tutor"));
    // eslint-disable-next-line
  }, [user, location.search]);

  function loadEntitlement() {
    axios.get(`${API}/tutor/entitlement`, getAuthConfig())
      .then((r) => setEntData(r.data))
      .catch(() => {});
  }

  const ent = entData?.entitlement;
  const usage = entData?.usage;
  const freeSubject = entData?.free_subject;
  const subjectLocked = (s) => ent && !ent.all_subjects && freeSubject && s !== freeSubject;
  const openPlans = (reason) => setPlansOpen({ reason });

  function loadSessions() {
    axios.get(`${API}/tutor/sessions`, getAuthConfig())
      .then((r) => setSessions(r.data?.sessions || []))
      .catch(() => {});
    axios.get(`${API}/tutor/streak`, getAuthConfig())
      .then((r) => {
        setStreakInfo(r.data);
        if (r.data?.new_badges?.length) setNewBadges((cur) => cur || r.data.new_badges);
      })
      .catch(() => {});
  }

  function handleLogin() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin("/tutor");
  }

  async function handleLogout() {
    try { await axios.post(`${API}/user-auth/logout`, {}, getAuthConfig()); } catch { /* noop */ }
    sessionStorage.removeItem("user_data");
    localStorage.removeItem("ai_tools_token");
    setUser(null); setProfile(null); setSession(null); setSignedOut(true);
  }

  async function startSession(mode, chapter) {
    setStarting(mode + (chapter || ""));
    try {
      const r = await axios.post(`${API}/tutor/sessions`, { subject: activeSubject, chapter: chapter || null, mode }, getAuthConfig());
      setSession({ ...r.data.session, fresh: true });
      loadSessions();
      if (ent && !ent.all_subjects) loadEntitlement();
    } catch (e) {
      if (!e?.paywalled) alert(errMsg(e, "Could not start session"));
    } finally { setStarting(""); }
  }

  const board = profile?.board || "cbse";
  const classTree = syllabus?.tree?.[board]?.[String(profile?.class_level)] || {};
  const availableSubjects = Object.keys(classTree);
  const activeSubject = availableSubjects.includes(subject) ? subject : (availableSubjects[0] || "maths");
  const chapters = classTree[activeSubject] || [];
  const compEligible = JEE_SUBJECTS.includes(activeSubject) || NEET_SUBJECTS.includes(activeSubject);

  if (checking) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={28}/></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4 text-center" data-testid="tutor-login-gate">
        {signedOut && (
          <p className="mb-6 px-4 py-2 rounded-full text-sm bg-emerald-500/10 border border-emerald-400/40 text-emerald-300" data-testid="signed-out-msg">
            ✓ You've been signed out
          </p>
        )}
        <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center mb-6">
          <GraduationCap className="text-cyan-300" size={30}/>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-3">Shanso<span className="text-cyan-300">Tutor</span></h1>
        <p className="text-zinc-400 max-w-md mb-2 text-base">Your personal AI tuition teacher — Classes 6 to 12, all subjects, CBSE &amp; Tamil Nadu State Board.</p>
        <p className="text-zinc-500 max-w-md mb-8 text-sm">Interactive lessons, doubt solving, and a teacher who actually talks and listens.</p>
        <button onClick={handleLogin} data-testid="tutor-login-btn"
          className="bg-white text-black font-semibold px-8 py-4 rounded-full hover:bg-zinc-200 transition-colors flex items-center gap-3">
          <Sparkles size={16}/> Continue with Google
        </button>
        <p className="text-zinc-600 text-xs mt-6">A parent's Google account is used to sign in — progress is visible to parents.</p>
        {signedOut && <p className="text-zinc-600 text-xs mt-2">Want a different account? Pick it on the Google screen after clicking above.</p>}
      </div>
    );
  }

  if (profile === null) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={28}/></div>;
  }

  if (profile === false || editingProfile) {
    return <TutorOnboarding api={API} authConfig={getAuthConfig()} initial={editingProfile ? profile : null}
      onDone={(p) => { setProfile(p); setEditingProfile(false); setSubject("maths"); }} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="tutor-page">
      {plansOpen && (
        <TutorPlans api={API} authConfig={getAuthConfig()} entitlement={ent} plans={entData?.plans} reason={plansOpen.reason}
          onClose={() => setPlansOpen(null)}/>
      )}
      {paidBanner && (
        <div className="bg-emerald-500/15 border-b border-emerald-400/30 text-emerald-200 text-sm text-center py-2 px-4 flex items-center justify-center gap-3" data-testid="paid-banner">
          <Crown size={14}/> {paidBanner}
          <button onClick={() => setPaidBanner("")} className="text-emerald-300/70 hover:text-white"><X size={14}/></button>
        </div>
      )}
      {newBadges?.length > 0 && profile && (
        <BadgeCelebration badges={newBadges} studentName={profile.student_name} language={profile.language} api={API} authConfig={getAuthConfig()}
          onClose={() => {
            axios.post(`${API}/tutor/badges/ack`, { badge_ids: newBadges.map((b) => b.id) }, getAuthConfig()).catch(() => {});
            setNewBadges(null);
          }}/>
      )}
      {mockTest && (
        <MockTest api={API} authConfig={getAuthConfig()} subject={mockTest.subject} chapter={mockTest.chapter}
          onClose={() => { setMockTest(null); loadSessions(); loadEntitlement(); }}/>
      )}
      {mockSetup && !mockTest && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4" data-testid="mock-setup-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-6 relative">
            <button onClick={() => setMockSetup(false)} data-testid="mock-setup-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
            <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><ClipboardCheck size={13}/> Mock test</p>
            <h3 className="text-white font-bold text-lg mb-2">20-mark timed mini paper</h3>
            <p className="text-zinc-400 text-xs mb-5">8 written questions (1, 2, 3 &amp; 5 marks) · 40 minutes · marked like the CBSE scheme · report-card PDF for parents.</p>
            <p className="text-xs font-semibold text-zinc-500 mb-1.5">Chapter</p>
            <select value={mockChapter} onChange={(e) => setMockChapter(e.target.value)} data-testid="mock-chapter-select"
              className="w-full mb-5 bg-[#111] border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:border-emerald-400 outline-none">
              <option value="">Full syllabus mix</option>
              {chapters.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
            </select>
            <button onClick={() => { setMockTest({ subject: activeSubject, chapter: mockChapter || null }); setMockSetup(false); }}
              data-testid="mock-start-btn"
              className="w-full py-3 rounded-full text-sm font-bold text-black" style={{ background: "linear-gradient(135deg, #10B981, #34D399)" }}>
              Start the paper — timer begins when it's ready
            </button>
          </div>
        </div>
      )}
      {compQuiz && (
        <CompetitiveQuiz api={API} authConfig={getAuthConfig()} exam={compQuiz.exam} subject={compQuiz.subject}
          chapter={compQuiz.chapter} onClose={() => { setCompQuiz(null); loadSessions(); loadEntitlement(); }}/>
      )}
      {compSetup && !compQuiz && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4" data-testid="comp-setup-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-6 relative">
            <button onClick={() => setCompSetup(false)} data-testid="comp-setup-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
            <p className="text-violet-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><Target size={13}/> Competitive practice</p>
            <h3 className="text-white font-bold text-lg mb-5">Pick your target exam</h3>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {["jee", "neet"].map((ex) => {
                const disabled = ex === "neet" ? !NEET_SUBJECTS.includes(activeSubject) : !JEE_SUBJECTS.includes(activeSubject);
                return (
                  <button key={ex} disabled={disabled} onClick={() => setCompExam(ex)} data-testid={`comp-exam-${ex}`}
                    className={`py-3 rounded-xl border text-sm font-black transition-colors ${compExam === ex
                      ? "bg-violet-500/15 border-violet-400 text-violet-200"
                      : disabled ? "border-zinc-800 text-zinc-700 cursor-not-allowed" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                    {ex === "jee" ? "JEE Main" : "NEET"}
                  </button>
                );
              })}
            </div>
            {activeSubject === "maths" && <p className="text-zinc-500 text-xs mb-4 -mt-2">NEET doesn't include Maths — switch to Physics/Chemistry/Biology for NEET practice.</p>}
            {activeSubject === "biology" && <p className="text-zinc-500 text-xs mb-4 -mt-2">JEE doesn't include Biology — switch to Physics/Chemistry/Maths for JEE practice.</p>}
            <p className="text-xs font-semibold text-zinc-500 mb-1.5">Chapter (optional)</p>
            <select value={compChapter} onChange={(e) => setCompChapter(e.target.value)} data-testid="comp-chapter-select"
              className="w-full mb-5 bg-[#111] border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:border-violet-400 outline-none">
              <option value="">Full syllabus mix</option>
              {chapters.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
            </select>
            <button onClick={() => { setCompQuiz({ exam: compExam, subject: activeSubject, chapter: compChapter || null }); setCompSetup(false); }}
              data-testid="comp-start-btn"
              className="w-full py-3 rounded-full text-sm font-bold text-white" style={{ background: "linear-gradient(135deg, #7C3AED, #A78BFA)" }}>
              Start 5 {compExam === "jee" ? "JEE" : "NEET"}-level questions
            </button>
          </div>
        </div>
      )}
      <header className="border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur z-30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center">
            <GraduationCap className="text-cyan-300" size={18}/>
          </div>
          <div>
            <p className="font-bold leading-tight">Shanso<span className="text-cyan-300">Tutor</span></p>
            <p className="text-[11px] text-zinc-500" data-testid="tutor-student-info">
              {profile.student_name} · Class {profile.class_level} · {BOARD_LABELS[board] || "CBSE"}
              <button onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn" className="ml-2 text-cyan-400/80 hover:text-cyan-300 underline underline-offset-2">edit</button>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ent && (
            <button onClick={() => openPlans(null)} data-testid="plan-badge"
              title={ent.source === "trial" ? `Plus trial — ${ent.trial_days_left} day${ent.trial_days_left === 1 ? "" : "s"} left` : `${ent.label} plan`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black border transition-colors ${
                ent.tier === "pro" ? "bg-amber-500/15 border-amber-400/50 text-amber-300"
                : ent.tier === "plus" ? "bg-cyan-500/15 border-cyan-400/50 text-cyan-200"
                : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
              {ent.tier === "pro" ? <Crown size={12}/> : <Sparkles size={12}/>}
              {ent.source === "trial" ? `Trial · ${ent.trial_days_left}d` : ent.label}
              {ent.tier !== "pro" && <span className="hidden sm:inline text-[10px] font-semibold opacity-80">· Upgrade</span>}
            </button>
          )}
          {streakInfo && (
            <div data-testid="streak-flame" title={streakInfo.studied_today ? `${streakInfo.current_streak}-day streak — you studied today!` : `${streakInfo.current_streak}-day streak — study today to keep it alive!`}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-black border ${streakInfo.studied_today
                ? "bg-orange-500/15 border-orange-400/50 text-orange-300"
                : "border-zinc-700 text-zinc-500"}`}>
              <Flame size={13} className={streakInfo.studied_today ? "text-orange-400" : "text-zinc-600"}/>
              {streakInfo.current_streak}
            </div>
          )}
          <button onClick={() => { setTab("learn"); }} data-testid="tab-learn"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${tab === "learn" ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            <BookOpen size={12} className="inline mr-1 -mt-0.5"/>Learn
          </button>
          <button onClick={() => setTab("progress")} data-testid="tab-progress"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${tab === "progress" ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            <BarChart3 size={12} className="inline mr-1 -mt-0.5"/>Progress
          </button>
          <button onClick={handleLogout} data-testid="tutor-logout-btn" className="p-2 text-zinc-500 hover:text-white" title="Logout"><LogOut size={15}/></button>
        </div>
      </header>

      {tab === "progress" ? (
        <TutorProgress api={API} authConfig={getAuthConfig()} />
      ) : session ? (
        <TutorChat api={API} authConfig={getAuthConfig()} session={session} autoplayAllowed={ent ? ent.voice_autoplay : true}
          onBack={() => { setSession(null); loadSessions(); loadEntitlement(); }} studentName={profile.student_name} language={profile.language}/>
      ) : (
        <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
          {ent && ent.tier === "free" && usage && (
            <div className="mb-5 px-4 py-3 rounded-2xl border border-zinc-800 bg-[#0A0A0A] flex flex-wrap items-center justify-between gap-3 text-xs" data-testid="free-usage-bar">
              <span className="text-zinc-400">
                Free plan · <span className="text-white font-bold">{Math.max(0, ent.messages_per_day - usage.messages_today)}</span> of {ent.messages_per_day} teacher replies left today
                {freeSubject
                  ? <> · subject: <span className="text-white font-bold">{subjectLabel(freeSubject)}</span></>
                  : <> · <span className="text-amber-300 font-semibold" data-testid="free-subject-hint">1 subject — the first one you start becomes your Free subject</span></>}
              </span>
              <button onClick={() => openPlans(null)} data-testid="free-upgrade-btn" className="px-3 py-1.5 rounded-full bg-cyan-400 text-black font-bold hover:bg-cyan-300">
                Unlock all subjects — ₹499/mo
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {availableSubjects.map((s) => (
              <button key={s} onClick={() => subjectLocked(s) ? openPlans(`Free plan covers one subject (${subjectLabel(freeSubject)}). Upgrade to Plus to learn every subject.`) : setSubject(s)} data-testid={`subject-${s}`}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors flex items-center gap-1.5 ${activeSubject === s ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : subjectLocked(s) ? "border-zinc-800 text-zinc-600 hover:border-zinc-700" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                {subjectLocked(s) && <Lock size={11} data-testid={`subject-lock-${s}`}/>}
                {subjectLabel(s)}
              </button>
            ))}
          </div>

          <button onClick={() => startSession("doubt")} disabled={!!starting} data-testid="ask-doubt-btn"
            className="w-full mb-3 p-5 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/10 to-purple-500/5 hover:border-cyan-400/60 transition-colors text-left flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0">
              {starting === "doubt" ? <Loader2 size={20} className="animate-spin text-cyan-300"/> : <MessageCircleQuestion size={20} className="text-cyan-300"/>}
            </div>
            <div>
              <p className="font-bold text-white">Ask a doubt</p>
              <p className="text-zinc-400 text-sm">Stuck on a question or concept? Type it, speak it — teacher will guide you step by step.</p>
            </div>
          </button>

          {(profile.class_level === 10 || profile.class_level === 12) && (
            <button onClick={() => ent && !ent.exam_prep ? openPlans("Board Exam Practice is a Plus feature — examiner-style questions marked step by step.") : startSession("exam")} disabled={!!starting} data-testid="exam-prep-btn"
              className="w-full mb-3 p-5 rounded-2xl border border-amber-400/35 bg-gradient-to-r from-amber-500/10 to-red-500/5 hover:border-amber-400/60 transition-colors text-left flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                {starting === "exam" ? <Loader2 size={20} className="animate-spin text-amber-300"/> : <FileText size={20} className="text-amber-300"/>}
              </div>
              <div className="flex-1">
                <p className="font-bold text-white">Board Exam Practice <span className="text-[10px] bg-amber-400 text-black px-1.5 py-0.5 rounded-full font-black ml-1 align-middle">CLASS {profile.class_level}</span></p>
                <p className="text-zinc-400 text-sm">Real board-pattern questions (1, 2, 3 &amp; 5 marks) — answered like an examiner, marked step by step like the {BOARD_LABELS[board] || "CBSE"} marking scheme.</p>
              </div>
              {ent && !ent.exam_prep && <span className="shrink-0 flex items-center gap-1 text-[10px] font-black text-amber-300 border border-amber-400/40 rounded-full px-2 py-1" data-testid="exam-lock"><Lock size={10}/> PLUS</span>}
            </button>
          )}
          {(profile.class_level === 11 || profile.class_level === 12) && compEligible && (
            <button onClick={() => { if (ent && !ent.competitive) { openPlans("JEE/NEET Prep with the rank estimator is a Pro feature."); return; } setCompExam(NEET_SUBJECTS.includes(activeSubject) ? (JEE_SUBJECTS.includes(activeSubject) ? compExam : "neet") : "jee"); setCompChapter(""); setCompSetup(true); }}
              data-testid="jee-neet-prep-btn"
              className="w-full mb-3 p-5 rounded-2xl border border-violet-400/35 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/5 hover:border-violet-400/60 transition-colors text-left flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
                <Target size={20} className="text-violet-300"/>
              </div>
              <div className="flex-1">
                <p className="font-bold text-white">JEE/NEET Prep <span className="text-[10px] bg-violet-400 text-black px-1.5 py-0.5 rounded-full font-black ml-1 align-middle">BEYOND NCERT</span></p>
                <p className="text-zinc-400 text-sm">Competitive-pattern practice — previous-year style MCQs, assertion-reason &amp; tricky numericals with +4/−1 marking.</p>
              </div>
              {ent && !ent.competitive && <span className="shrink-0 flex items-center gap-1 text-[10px] font-black text-amber-300 border border-amber-400/40 rounded-full px-2 py-1" data-testid="comp-lock"><Lock size={10}/> PRO</span>}
            </button>
          )}
          <button onClick={() => {
              if (ent && ent.mocktests_per_month === 0) { openPlans("Timed mock tests with a report-card PDF are a Plus feature (2 a month)."); return; }
              if (ent && usage && ent.mocktests_per_month > 0 && usage.mocktests_month >= ent.mocktests_per_month) { openPlans("Plus includes 2 mock tests a month — you've used both. Pro gives unlimited mock tests."); return; }
              setMockChapter(""); setMockSetup(true);
            }} data-testid="mock-test-btn"
            className="w-full mb-3 p-5 rounded-2xl border border-emerald-400/35 bg-gradient-to-r from-emerald-500/10 to-teal-500/5 hover:border-emerald-400/60 transition-colors text-left flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <ClipboardCheck size={20} className="text-emerald-300"/>
            </div>
            <div className="flex-1">
              <p className="font-bold text-white">Mock Test <span className="text-[10px] bg-emerald-400 text-black px-1.5 py-0.5 rounded-full font-black ml-1 align-middle">20 MARKS · 40 MIN</span></p>
              <p className="text-zinc-400 text-sm">Timed mini paper with written answers — marked like the board marking scheme, with a report-card PDF for parents.</p>
            </div>
            {ent && ent.mocktests_per_month === 0 && <span className="shrink-0 flex items-center gap-1 text-[10px] font-black text-amber-300 border border-amber-400/40 rounded-full px-2 py-1" data-testid="mock-lock"><Lock size={10}/> PLUS</span>}
            {ent && usage && ent.mocktests_per_month > 0 && <span className="shrink-0 text-[10px] font-bold text-zinc-500" data-testid="mock-quota">{Math.max(0, ent.mocktests_per_month - usage.mocktests_month)}/{ent.mocktests_per_month} left this month</span>}
          </button>
          <div className="mb-5"/>

          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">
            Class {profile.class_level} {subjectLabel(activeSubject)} — pick a chapter to learn
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="chapter-list">
            {chapters.map((ch, i) => (
              <button key={ch} onClick={() => startSession("chapter", ch)} disabled={!!starting} data-testid={`chapter-${i}`}
                className="text-left px-4 py-3 rounded-xl border border-zinc-800 bg-[#0A0A0A] hover:border-cyan-400/50 transition-colors flex items-center gap-3">
                <span className="text-cyan-300/70 font-mono text-xs w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm text-zinc-200">{ch}</span>
                {starting === "chapter" + ch && <Loader2 size={14} className="animate-spin text-cyan-300 ml-auto"/>}
              </button>
            ))}
          </div>

          {sessions.length > 0 && (
            <div className="mt-10">
              <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">Continue where you left off</p>
              <div className="space-y-2" data-testid="recent-sessions">
                {sessions.slice(0, 6).map((s) => (
                  <button key={s.session_id} onClick={() => setSession(s)} data-testid={`resume-${s.session_id}`}
                    className="w-full text-left px-4 py-3 rounded-xl border border-zinc-800 bg-[#0A0A0A] hover:border-zinc-600 transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-200 truncate">{s.title}</p>
                      <p className="text-[11px] text-zinc-500">{subjectLabel(s.subject)} · {s.mode === "chapter" ? "Chapter lesson" : s.mode === "exam" ? "Exam practice" : "Doubt"} · {s.message_count} replies</p>
                    </div>
                    <span className="text-cyan-300 text-xs shrink-0">Resume →</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>
      )}
    </div>
  );
}
